import { InputText } from 'primereact/inputtext';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AppButton } from '../buttons';
import { FlexContainer } from '../styled-ui';

const StyledDropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledDropdownInput = styled.div<{ isOpen?: boolean }>`
  border: 1px solid var(--beige-main);
  background-color: var(--beige-main);
  border-radius: var(--default-border-radius);

  padding: var(--small-padding) var(--medium-padding);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  min-height: 48px;
  padding: 10px var(--default-padding);
  transition: background-color 0.2s, color 0.2s, border-color 0.2s,
    box-shadow 0.2s;
  box-shadow: ${(props) =>
    props.isOpen ? 'var(--box-shadow-red-no-blur)' : 'none'};
  border-color: ${(props) =>
    props.isOpen ? 'var(--red-main)' : 'var(--beige-main)'};

  i {
    color: var(--gray-darker);
    font-size: var(--small-font-size);
  }

  &:hover {
    border-color: var(--red-main) !important;
  }
`;

const StyledDropdownLabel = styled.span`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  color: var(--black-main);

  &.placeholder {
    color: var(--gray-darker);
  }
`;

const StyledChevronIcon = styled.i`
  font-size: var(--small-font-size);
  transition: transform 0.2s ease;

  &.p-dropdown-open {
    transform: rotate(180deg);
  }
`;

const StyledOverlay = styled.div<{
  isOpen: boolean;
  width: number;
  top: number;
  left: number;
}>`
  position: fixed;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  width: ${(props) => props.width}px;
  z-index: 1000;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transform: ${(props) =>
    props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  pointer-events: ${(props) => (props.isOpen ? 'auto' : 'none')};
  border-radius: var(--xsmall-border-radius);
  box-shadow: 0px 4px 32px 0px rgba(0, 0, 0, 0.25);

  button {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    min-width: 130px;
  }
`;

const StyledOverlayContent = styled.div`
  padding: 0;
  border-radius: var(--xsmall-border-radius);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--gray-pale);
  background: var(--white-main);
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow: hidden;
`;

const StyledFilterInput = styled.div`
  padding: var(--small-padding) var(--default-padding-small);
  border-bottom: 1px solid var(--gray-pale);
  flex-shrink: 0;

  .p-inputtext {
    width: 100%;
    padding: var(--xsmall-padding) var(--small-padding);
    font-size: var(--small-font-size);
    border: 1px solid var(--gray-pale);
    border-radius: var(--small-border-radius);

    &:focus {
      border-color: var(--red-main);
      box-shadow: var(--box-shadow-red-no-blur);
    }
  }
`;

const StyledItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  scrollbar-width: none;
`;

const StyledItem = styled.div<{ isEditing?: boolean; isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--medium-padding) 20px;
  border-bottom: 1px solid var(--gray-pale);
  transition: all 0.2s ease;
  height: 56px;
  cursor: ${(props) => (props.isEditing ? 'default' : 'pointer')};
  border-radius: 4px;
  color: ${(props) =>
    props.isSelected ? 'var(--red-main)' : 'var(--black-main)'};
  background-color: ${(props) =>
    props.isSelected ? 'var(--gray-pale)' : 'transparent'};

  &:hover,
  &:hover {
    color: var(--red-main);
    background: var(--gray-pale);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StyledItemName = styled.span`
  flex: 1;
  font-weight: 500;
  padding: var(--xxsmall-padding) 0;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 400;
`;

const StyledCreateNewItem = styled.div`
  display: flex;
  align-items: center;
  padding: var(--medium-padding) 20px;
  border-top: 1px solid var(--gray-pale);
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 70px;
  background: var(--white-main);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 400;
  flex-shrink: 0;

  &:hover {
    background-color: var(--gray-pale);
    border-color: var(--gray-pale);
  }
`;

const StyledCreateIcon = styled.i`
  color: var(--gray-darker);
  line-height: var(--small-line-height);
  margin-right: 6px;
  font-size: 8px;
  font-weight: bold;
  transition: color 0.2s ease;
`;

const StyledCreateText = styled.span`
  color: var(--gray-darker);
  font-weight: 500;
  transition: color 0.2s ease;
`;

const StyledNewItemInput = styled.div`
  padding: var(--medium-padding) 20px;
  border-top: 1px solid var(--gray-pale);
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 70px;
  background: var(--white-main);
  flex-shrink: 0;
`;

const StyledInput = styled(InputText)`
  flex: 1;
  padding: 0 var(--small-padding);
  transition: all 0.2s ease;
  border: none !important;
  box-shadow: none !important;

  &.new {
    padding: 0;
    background: transparent !important;
    height: 30px;
  }
`;

export type EditableDropdownItem = {
  id: string;
  name: string;
};

type EditableDropdownProps = {
  value?: string;
  options?: EditableDropdownItem[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isLoading?: boolean;
  createButtonText?: string;
  onChange?: (value: EditableDropdownItem | null) => void;
  onCreateNew?: (name: string) => void;
  onEditName?: (item: EditableDropdownItem, newName: string) => void;
};

export const EditableDropdown: React.FC<EditableDropdownProps> = ({
  value,
  options = [],
  placeholder,
  disabled = false,
  className,
  isLoading,
  createButtonText,
  onChange,
  onCreateNew,
  onEditName,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [editingItem, setEditingItem] = useState<EditableDropdownItem | null>(
    null,
  );
  const [editingName, setEditingName] = useState('');
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);
  const [overlayPosition, setOverlayPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const newItemRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const updateOverlayPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scrollTop = document.documentElement.scrollTop;
      const scrollLeft = document.documentElement.scrollLeft;
      const bodyTop = rect.top + scrollTop;
      const bodyLeft = rect.left + scrollLeft;
      const bodyBottom = rect.bottom + scrollTop;

      const viewportHeight = window.innerHeight;
      const dropdownHeight = 300;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Open above if not enough space below
      const shouldOpenAbove =
        spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight;

      setOverlayPosition({
        top: shouldOpenAbove ? bodyTop - dropdownHeight + 36 : bodyBottom + 11,
        left: bodyLeft,
        width: rect.width,
      });
    }
  };

  const filteredOptions = filterText
    ? options.filter((item) =>
        item.name.toLowerCase().includes(filterText.toLowerCase()),
      )
    : options;

  const handleDropdownClick = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);

      if (newIsOpen) {
        setFilterText('');
        updateOverlayPosition();

        setTimeout(() => {
          filterInputRef.current?.focus();
        }, 50);

        // Scroll to selected item when opening
        setTimeout(() => {
          if (value && selectedItemRef.current) {
            selectedItemRef.current.scrollIntoView({
              behavior: 'auto',
              block: 'nearest',
            });
          }
        }, 0);
      }
    }
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setNewItemName('');

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handleSaveNew = () => {
    if (newItemName.trim() && onCreateNew) {
      onCreateNew(newItemName.trim());
      setNewItemName('');
      setIsCreating(false);

      // Scroll to new item
      setTimeout(() => {
        if (newItemRef.current) {
          newItemRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 100);
    }
  };

  const handleEditName = (item: EditableDropdownItem) => {
    setEditingItem(item);
    setEditingName(item.name);
  };

  const handleSaveEdit = () => {
    if (editingItem && editingName.trim() && onEditName) {
      onEditName(editingItem, editingName.trim());
      setEditingItem(null);
      setEditingName('');
    }
  };

  const handleSelectItem = (item: EditableDropdownItem) => {
    if (onChange) {
      onChange(item);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const isWithinDropdown = target.closest('.editable-dropdown-container');
      const isWithinOverlay = target.closest(
        '[data-overlay="editable-dropdown"]',
      );

      if (!isWithinDropdown && !isWithinOverlay) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      if (event.target === event.currentTarget) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isCreating) {
          setIsCreating(false);
          setNewItemName('');
        } else if (editingItem) {
          setEditingItem(null);
          setEditingName('');
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      const contentElements = document.querySelectorAll('.content');
      contentElements.forEach((element) => {
        element.addEventListener('scroll', handleScroll, {
          passive: true,
          capture: true,
        });
      });

      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);

      const contentElements = document.querySelectorAll('.content');
      contentElements.forEach((element) => {
        element.removeEventListener('scroll', handleScroll, { capture: true });
      });

      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, isCreating, editingItem]);

  return (
    <>
      <StyledDropdownContainer
        ref={containerRef}
        className={`editable-dropdown-container ${className}`}
      >
        <StyledDropdownInput isOpen={isOpen} onClick={handleDropdownClick}>
          <StyledDropdownLabel className={!value ? 'placeholder' : ''}>
            {value
              ? options.find((item) => item.id === value)?.name
              : placeholder}
          </StyledDropdownLabel>
          <StyledChevronIcon
            className={
              isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-down'
            }
          />
        </StyledDropdownInput>
      </StyledDropdownContainer>

      {isOpen &&
        createPortal(
          <StyledOverlay
            isOpen={isOpen}
            width={overlayPosition.width}
            top={overlayPosition.top}
            left={overlayPosition.left}
            data-overlay="editable-dropdown"
          >
            <StyledOverlayContent>
              <StyledFilterInput>
                <InputText
                  ref={filterInputRef}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder={t('generic.search')}
                />
              </StyledFilterInput>
              <StyledItemList>
                {filteredOptions.map((item, index) => (
                  <StyledItem
                    key={item.id}
                    ref={
                      index === filteredOptions.length - 1
                        ? newItemRef
                        : item.id === value
                        ? selectedItemRef
                        : null
                    }
                    isEditing={editingItem?.id === item.id}
                    isSelected={item.id === value}
                    onClick={
                      editingItem?.id === item.id
                        ? undefined
                        : () => handleSelectItem(item)
                    }
                  >
                    {editingItem?.id === item.id ? (
                      <FlexContainer gap={12}>
                        <StyledInput
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          autoFocus
                        />
                        <AppButton
                          onClick={handleSaveEdit}
                          isDisabled={!editingName.trim()}
                          label={t('button.save')}
                          type="outlined"
                          severity="secondary"
                          size="sm"
                        />
                      </FlexContainer>
                    ) : (
                      <>
                        <StyledItemName>{item.name}</StyledItemName>
                        <AppButton
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleEditName(item);
                          }}
                          label={t('button.edit.name')}
                          type="outlined"
                          severity="secondary"
                          size="sm"
                        />
                      </>
                    )}
                  </StyledItem>
                ))}
              </StyledItemList>

              {isCreating ? (
                <StyledNewItemInput>
                  <FlexContainer>
                    <StyledCreateIcon className="pi pi-plus" />
                    <StyledInput
                      ref={inputRef}
                      className="new"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      placeholder={t('generic.createNew')}
                    />
                  </FlexContainer>
                  <AppButton
                    onClick={handleSaveNew}
                    isDisabled={!newItemName.trim()}
                    label={t('button.save')}
                    type="outlined"
                    severity="secondary"
                    size="sm"
                  />
                </StyledNewItemInput>
              ) : (
                <StyledCreateNewItem onClick={handleCreateNew}>
                  <StyledCreateIcon className="pi pi-plus" />
                  <StyledCreateText>{createButtonText}</StyledCreateText>
                </StyledCreateNewItem>
              )}
            </StyledOverlayContent>
          </StyledOverlay>,
          document.body,
        )}
    </>
  );
};
